import type { Condition } from '@medplum/fhirtypes';
import { ckdOngoingMeasurementsProtocol } from './definitions/ckdContext';
import { ckdStageMonitoringProtocol } from './definitions/ckdStageContext';
import { hyperlipidemiaMonitoringProtocol } from './definitions/hyperlipidemiaContext';
import { hypertensionMonitoringProtocol } from './definitions/hypertensionContext';
import { masldOngoingMeasurementsProtocol } from './definitions/masldContext';
import { obesityOngoingMeasurementsProtocol } from './definitions/obesityContext';
import { asthmaOngoingMeasurementsProtocol, copdOngoingMeasurementsProtocol } from './definitions/respiratoryContext';
import {
  hyperthyroidismOngoingMeasurementsProtocol,
  hypothyroidismOngoingMeasurementsProtocol,
} from './definitions/thyroidContext';
import { t2dmOngoingMeasurementsProtocol } from './definitions/t2dmContext';
import { matchProtocols } from './matching';
import type { ProtocolDeliveryMode } from './extensions';
import type { ProtocolSpec } from './planner/plan';

/** A protocol a condition link applies, and how its actions should reach the plan. */
export interface ConditionLinkEntry {
  spec: ProtocolSpec;
  /**
   * `suggest` proposes the actions for a clinician to accept; `commit` writes them straight onto the
   * plan. Recorded per entry rather than per path, so a protocol trusted enough to write directly can
   * be changed here, in the list where that decision is made, rather than by branching at the caller.
   */
  delivery: ProtocolDeliveryMode;
}

/**
 * The protocols a condition link applies. Curated rather than derived: whether linking a diagnosis
 * should pull a protocol in is a product decision about that protocol, not something readable off its
 * definition, and the list is short enough to be worth reading directly.
 *
 * Every entry is `suggest` today. A condition link fires inside an agent turn, which is what gives a
 * suggestion somewhere to surface — the care-plan board filters suggestions to the open conversation,
 * so one raised without a run has nowhere to be seen (#10084). Writing straight onto the plan off a
 * diagnosis link would also skip the review that bootstrapping a fresh chart most needs.
 *
 * `t2dm-routine-monitoring` is deliberately absent: it is applied deliberately, from the care plan,
 * and firing it off a diagnosis link would raise a full monitoring plan nobody asked for.
 */
export const CONDITION_LINK_PROTOCOLS: readonly ConditionLinkEntry[] = [
  { spec: t2dmOngoingMeasurementsProtocol, delivery: 'suggest' },
  { spec: ckdOngoingMeasurementsProtocol, delivery: 'suggest' },
  { spec: obesityOngoingMeasurementsProtocol, delivery: 'suggest' },
  { spec: hypothyroidismOngoingMeasurementsProtocol, delivery: 'suggest' },
  { spec: hyperthyroidismOngoingMeasurementsProtocol, delivery: 'suggest' },
  { spec: copdOngoingMeasurementsProtocol, delivery: 'suggest' },
  { spec: asthmaOngoingMeasurementsProtocol, delivery: 'suggest' },
  { spec: masldOngoingMeasurementsProtocol, delivery: 'suggest' },
  { spec: ckdStageMonitoringProtocol, delivery: 'suggest' },
  { spec: hypertensionMonitoringProtocol, delivery: 'suggest' },
  { spec: hyperlipidemiaMonitoringProtocol, delivery: 'suggest' },
];

/**
 * The protocols that apply to a Condition, by their `useContext[focus]` codings. More than one can
 * match — a chart carrying several diagnoses under one topic — so every match is returned.
 */
export const protocolsForCondition = (condition: Condition): ConditionLinkEntry[] =>
  matchProtocols(
    condition,
    CONDITION_LINK_PROTOCOLS.map((e) => e.spec.planDefinition),
  ).flatMap((pd) => CONDITION_LINK_PROTOCOLS.filter((e) => e.spec.planDefinition === pd));
